import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Search, Filter, ChevronDown, ChevronUp, KeyRound, Lock, ShieldCheck, Users as UsersIcon } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi , useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { formatDateTime } from '@/lib/dateFormat';
import { PasswordResetModal } from '@/components/PasswordResetModal';
import { TableRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { PhoneInput, PhoneValue, parsePhoneString, formatPhoneString } from '@/components/ui/PhoneInput';
import { SelectField } from '@/components/ui/SelectField';

interface SaUser {
  id: string; name: string; email: string; phone?: string | null; role: string;
  isActive: boolean; lastLogin: string | null; createdAt: string;
}

interface PlatformRoleLite {
  id: string; name: string; isActive: boolean;
}

// Legacy role keys some existing rows may still carry — render a
// friendly label until the boot backfill rewrites them. New rows
// will store the real PlatformRole.name as the value.
const LEGACY_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  support_agent: 'Support',
  finance_admin: 'Billing Manager',
};
const roleLabel = (raw: string) => LEGACY_LABELS[raw] ?? raw;

// ── User Form Drawer ───────────────────────────────────────────────────────────
function UserDrawer({
  user, roles, onClose, onSaved,
}: {
  user?: SaUser; roles: PlatformRoleLite[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!user;
  // Default to the first active role from the DB rather than a hardcoded
  // value. If the user being edited has a legacy role key, keep it so
  // the dropdown can render the friendly label until the backfill runs.
  const defaultRole = user?.role ?? roles.find(r => r.isActive)?.name ?? '';
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: defaultRole,
    isActive: user?.isActive ?? true,
  });
  // ISD code (from geo master) + number, composed into a single string on save.
  const [phoneVal, setPhoneVal] = useState<PhoneValue>(() => parsePhoneString(user?.phone || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    if (!isEdit && !form.password) { setError('Password is required'); return; }
    setError('');
    setSaving(true);
    try {
      const phoneStr = phoneVal.number.trim() ? formatPhoneString(phoneVal) : '';
      if (isEdit) {
        const payload: any = { name: form.name, phone: phoneStr, role: form.role, isActive: form.isActive };
        await saApi.patch(`/super-admin/users/${user!.id}`, payload);
        toast('success', 'User updated');
      } else {
        await saApi.post('/super-admin/users', { name: form.name, email: form.email, phone: phoneStr, password: form.password, role: form.role });
        toast('success', 'User created');
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save user');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-[#94A3B8] transition-all';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full sm:w-[440px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#1E293B]">{isEdit ? 'Edit User' : 'New Admin User'}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#64748B] transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Full Name <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="John Doe" required value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Email <span className="text-red-500">*</span></label>
            <input type="email" className={`${inputCls} ${isEdit ? 'bg-gray-50 text-[#94A3B8]' : ''}`}
              placeholder="admin@company.com" required disabled={isEdit}
              value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Phone / WhatsApp</label>
            <PhoneInput value={phoneVal} onChange={setPhoneVal} placeholder="98765 43210" />
            <p className="text-[11px] text-slate-400">Used to alert this admin by email + WhatsApp when a demo is booked on them.</p>
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <label className="block text-xs font-normal text-[#64748B]">Password <span className="text-red-500">*</span></label>
              <input type="password" className={inputCls} placeholder="Min. 8 characters" required minLength={6}
                value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Role</label>
            <SelectField className={inputCls} value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
              {roles.filter(r => r.isActive).map(r => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
              {/* Preserve legacy role value on edit so admin can see what is currently saved. */}
              {form.role && !roles.some(r => r.name === form.role) && (
                <option value={form.role}>{roleLabel(form.role)} (legacy)</option>
              )}
            </SelectField>
            {roles.length === 0 && (
              <p className="text-[11px] text-slate-400">No roles found. Create roles in the Roles page first.</p>
            )}
          </div>

          {isEdit && (
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
              <div>
                <p className="text-sm font-medium text-[#1E293B]">Active</p>
                <p className="text-xs text-[#94A3B8]">Inactive users cannot log in</p>
              </div>
              <button type="button" onClick={() => setForm(p => ({ ...p, isActive: !p.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-[#6366F1]' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</div>
          )}
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-[#64748B] border border-gray-200 hover:border-gray-300 transition-all">
            Cancel
          </button>
          <button onClick={handleSubmit as any} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#6366F1] hover:bg-[#4F46E5] text-white transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Users Page ─────────────────────────────────────────────────────────────────
// Mirrors the vendor Users page: search, role/status filters, inline active
// toggle (with confirm), reset password, edit, delete. Your own account can't
// be deactivated or deleted.
export function SuperAdminUsersPage() {
  const { admin } = useSuperAdminAuth();
  const [users, setUsers] = useState<SaUser[]>([]);
  const [roles, setRoles] = useState<PlatformRoleLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<{ open: boolean; user?: SaUser }>({ open: false });
  const [confirm, setConfirm] = useState<{ type: 'toggle' | 'delete'; user: SaUser } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState<SaUser | null>(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch users + roles together so the role dropdown is populated
  // with the actual PlatformRole rows from the database, not a
  // hardcoded list.
  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      saApi.get('/super-admin/users'),
      saApi.get('/super-admin/roles'),
    ])
      .then(([usersRes, rolesRes]) => {
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        const rolesList = Array.isArray(rolesRes.data) ? rolesRes.data : [];
        setRoles(rolesList.map((r: any) => ({ id: r.id, name: r.name, isActive: r.isActive })));
      })
      .catch(() => toast('error', 'Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = users.filter(u =>
    (!q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.phone ?? '').toLowerCase().includes(q)) &&
    (!roleFilter || u.role === roleFilter) &&
    (statusFilter === '' || String(u.isActive) === statusFilter),
  );
  const { sorted, sort, onSort } = useSort(filtered);
  const hasActiveFilters = !!(q || roleFilter || statusFilter);
  const activeCount = users.filter(u => u.isActive).length;
  const roleOptions = Array.from(new Set([...roles.map(r => r.name), ...users.map(u => u.role)])).filter(Boolean);

  const isSelf = (u: SaUser) => u.email === admin?.email;

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === 'toggle') {
        await saApi.patch(`/super-admin/users/${confirm.user.id}`, { isActive: !confirm.user.isActive });
        setUsers(prev => prev.map(u => u.id === confirm.user.id ? { ...u, isActive: !u.isActive } : u));
        toast('success', `${confirm.user.name} ${confirm.user.isActive ? 'deactivated' : 'activated'}`);
      } else {
        await saApi.delete(`/super-admin/users/${confirm.user.id}`);
        setUsers(prev => prev.filter(u => u.id !== confirm.user.id));
        toast('success', 'User deleted');
      }
      setConfirm(null);
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Action failed');
    } finally { setConfirmLoading(false); }
  };

  const selectCls = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';

  return (
    <SuperAdminLayout title="Users">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Users</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {users.length} admin user{users.length !== 1 ? 's' : ''} · {activeCount} active
            </p>
          </div>
          <button onClick={() => setDrawer({ open: true })}
            className="flex items-center gap-2 px-4 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors flex-shrink-0">
            <Plus className="h-4 w-4" /> New User
          </button>
        </div>

        {/* Search + filter toggle */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="h-10 w-64 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              placeholder="Search name, email or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm ${showFilters ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <Filter className="h-4 w-4" /> Filters
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {hasActiveFilters && (
            <button
              onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); }}
              className="inline-flex h-10 items-center gap-1 px-2 text-sm text-slate-500 hover:text-slate-800"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Role</label>
              <SelectField className={selectCls} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {roleOptions.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Status</label>
              <SelectField className={selectCls} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </SelectField>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <table className="w-full"><tbody>
              {Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={7} />)}
            </tbody></table>
          ) : users.length === 0 ? (
            <EmptyState
              icon={UsersIcon}
              title="No admin users yet"
              description="Add a platform user so they can manage prospects, demos and tenants."
              action={
                <button onClick={() => setDrawer({ open: true })} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold">
                  <Plus className="h-3.5 w-3.5" /> New user
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <TableHeadRow className="border-b-2 border-slate-200">
                    <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
                    <SortTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
                    <SortTh label="Phone / WhatsApp" sortKey="phone" sort={sort} onSort={onSort} />
                    <SortTh label="Role" sortKey="role" sort={sort} onSort={onSort} />
                    <SortTh label="Status" sortKey="isActive" sort={sort} onSort={onSort} />
                    <SortTh label="Last Login" sortKey="lastLogin" sort={sort} onSort={onSort} />
                    <SortTh label="Actions" sortable={false} />
                  </TableHeadRow>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400">No users match these filters</td></tr>
                  ) : sorted.map(u => {
                    const self = isSelf(u);
                    return (
                      <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${self ? 'bg-indigo-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${self ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                              <span className={`text-xs font-semibold ${self ? 'text-indigo-600' : 'text-blue-600'}`}>{u.name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-slate-900">{u.name}</p>
                              {self && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                  <ShieldCheck className="h-2.5 w-2.5" /> You
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.phone || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-[#6366F1]">{roleLabel(u.role)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setConfirm({ type: 'toggle', user: u })}
                            disabled={self}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${u.isActive ? 'bg-[#6366F1]' : 'bg-slate-300'}`}
                            title={self ? 'You cannot deactivate your own account' : u.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${u.isActive ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{u.lastLogin ? formatDateTime(u.lastLogin) : 'Never'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setDrawer({ open: true, user: u })} title="Edit"
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setResetPwdUser(u)} title="Reset Password"
                              className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-600 transition-colors">
                              <KeyRound className="h-3.5 w-3.5" />
                            </button>
                            {self ? (
                              <span title="You cannot delete your own account" className="p-1.5 text-slate-300 cursor-default">
                                <Lock className="h-3.5 w-3.5" />
                              </span>
                            ) : (
                              <button onClick={() => setConfirm({ type: 'delete', user: u })} title="Delete"
                                className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {drawer.open && (
        <UserDrawer
          user={drawer.user}
          roles={roles}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); fetchAll(); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.type === 'toggle'
          ? (confirm.user.isActive ? 'Deactivate User?' : 'Activate User?')
          : 'Delete User?'}
        message={confirm?.type === 'toggle'
          ? (confirm.user.isActive
              ? `Are you sure you want to deactivate ${confirm.user.name}? They will not be able to login.`
              : `Are you sure you want to activate ${confirm.user.name}?`)
          : `Delete user "${confirm?.user.name}"? This cannot be undone.`}
        confirmText={confirm?.type === 'toggle' ? (confirm.user.isActive ? 'Deactivate' : 'Activate') : 'Delete'}
        confirmButtonColor="red"
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
        isLoading={confirmLoading}
      />
      <PasswordResetModal
        isOpen={!!resetPwdUser}
        title="Reset User Password"
        subject={resetPwdUser ? `${resetPwdUser.name} · ${resetPwdUser.email}` : ''}
        onConfirm={async (newPassword) => {
          if (!resetPwdUser) return;
          await saApi.post(`/super-admin/users/${resetPwdUser.id}/change-password`, { newPassword });
          toast('success', `Password reset for ${resetPwdUser.email}`);
        }}
        onClose={() => setResetPwdUser(null)}
      />
    </SuperAdminLayout>
  );
}
